'use client';

import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cn } from '@/lib/utils';

const failedAvatarSources = new Set<string>();

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      'relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-quaternary outline-1 -outline-offset-1 outline-border-secondary transition-transform hover:scale-105',
      className,
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, onError, src, ...props }, ref) => {
  const normalizedSrc = typeof src === 'string' ? src : undefined;
  const [effectiveSrc, setEffectiveSrc] = React.useState<string | undefined>(() =>
    normalizedSrc && failedAvatarSources.has(normalizedSrc) ? undefined : normalizedSrc,
  );

  React.useEffect(() => {
    if (!normalizedSrc || failedAvatarSources.has(normalizedSrc)) {
      setEffectiveSrc(undefined);
      return;
    }
    setEffectiveSrc(normalizedSrc);
  }, [normalizedSrc]);

  return (
    <AvatarPrimitive.Image
      ref={ref}
      className={cn('size-full rounded-full object-cover', className)}
      crossOrigin="anonymous"
      referrerPolicy="no-referrer"
      src={effectiveSrc}
      onError={(event) => {
        if (normalizedSrc) failedAvatarSources.add(normalizedSrc);
        setEffectiveSrc(undefined);
        onError?.(event);
      }}
      {...props}
    />
  );
});
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex size-full items-center justify-center rounded-full bg-brand-solid text-sm font-semibold uppercase text-white',
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
